import React, { useCallback, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContext, type OrganizationMembership, type StaffRole } from '@/contexts/AuthContext';
import { runPreSignoutHooks } from '@/lib/signoutHooks';

const POLISHED_VERSION_ORG_ID = 'a90138ee-26a5-4570-97b0-5a1144b9ad85';
const SELECTED_ORG_STORAGE_KEY = 'admin.selectedOrganizationId';
const UAT_REVIEWER_EMAILS = new Set(['a.ronna1017@gmail.com']);

const checkAdminAccess = async (userId: string): Promise<boolean> => {
  try {
    const [adminResult, superAdminResult] = await Promise.all([
      supabase.rpc('has_role', { _user_id: userId, _role: 'admin' }),
      supabase.rpc('has_role', { _user_id: userId, _role: 'super_admin' }),
    ]);

    if (adminResult.error) {
      console.error('Error checking admin role:', adminResult.error);
    }

    if (superAdminResult.error) {
      console.error('Error checking super admin role:', superAdminResult.error);
    }

    return Boolean(adminResult.data || superAdminResult.data);
  } catch (error) {
    console.error('Error checking admin access:', error);
    return false;
  }
};

const checkOrgMembership = async (
  userId: string
): Promise<{ isOwner: boolean; membership: OrganizationMembership | null; memberships: OrganizationMembership[] }> => {
  try {
    const { data, error } = await supabase
      .from('organization_members')
      .select(
        `
        organization_id,
        role,
        created_at,
        organizations (
          id,
          name
        )
      `
      )
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error checking org membership:', error);
      return { isOwner: false, membership: null, memberships: [] };
    }

    if (!data || data.length === 0) {
      return { isOwner: false, membership: null, memberships: [] };
    }

    const memberships: OrganizationMembership[] = data
      .filter((membership) => membership.organization_id)
      .map((membership) => ({
        organizationId: membership.organization_id,
        organizationName:
          membership.organizations && typeof membership.organizations === 'object'
            ? ((membership.organizations as { name?: string }).name ?? 'Your business')
            : 'Your business',
        role: membership.role as 'owner' | 'member',
      }));

    const selectedOrganizationId = localStorage.getItem(SELECTED_ORG_STORAGE_KEY);
    const isOwner = data.some((membership) => membership.role === 'owner');
    const preferredMembership =
      data.find((membership) => membership.organization_id === selectedOrganizationId) ??
      data.find((membership) => membership.organization_id === POLISHED_VERSION_ORG_ID) ??
      data.find((membership) => membership.role === 'owner' && membership.organization_id) ??
      data.find((membership) => membership.organization_id) ??
      data[0];

    const organizationName =
      preferredMembership.organizations && typeof preferredMembership.organizations === 'object'
        ? ((preferredMembership.organizations as { name?: string }).name ?? 'Your business')
        : 'Your business';

    return {
      isOwner,
      membership: {
        organizationId: preferredMembership.organization_id,
        organizationName,
        role: preferredMembership.role as 'owner' | 'member',
      },
      memberships,
    };
  } catch (error) {
    console.error('Error checking org membership:', error);
    return { isOwner: false, membership: null, memberships: [] };
  }
};

const checkTeamRole = async (userId: string): Promise<StaffRole> => {
  try {
    const { data, error } = await supabase.rpc('get_team_member_role', {
      _user_id: userId,
    });

    if (error) {
      console.error('Error checking team role:', error);
      return null;
    }

    const allowedRoles: StaffRole[] = ['super_admin', 'owner', 'supervisor', 'cleaner', null];
    return allowedRoles.includes(data as StaffRole) ? (data as StaffRole) : null;
  } catch (error) {
    console.error('Error checking team role:', error);
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isUatReviewer, setIsUatReviewer] = useState(false);
  const [staffRole, setStaffRole] = useState<StaffRole>(null);
  const [isOrgOwner, setIsOrgOwner] = useState(false);
  const [organizationMembership, setOrganizationMembership] = useState<OrganizationMembership | null>(null);
  const [organizationMemberships, setOrganizationMemberships] = useState<OrganizationMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);

  const rolesRequestIdRef = useRef(0);

  const refreshRolesForUser = useCallback(async (userId: string) => {
    const requestId = ++rolesRequestIdRef.current;
    setRolesLoading(true);

    try {
      const [hasAdminAccess, orgResult, teamRole] = await Promise.all([
        checkAdminAccess(userId),
        checkOrgMembership(userId),
        checkTeamRole(userId),
      ]);

      if (requestId !== rolesRequestIdRef.current) return;

      const derivedStaffRole: StaffRole = hasAdminAccess
        ? 'super_admin'
        : orgResult.isOwner || teamRole === 'owner'
          ? 'owner'
          : teamRole === 'supervisor' || teamRole === 'cleaner'
            ? teamRole
            : null;

      setIsSuperAdmin(hasAdminAccess);
      setIsOrgOwner(orgResult.isOwner || teamRole === 'owner');
      setOrganizationMembership(orgResult.membership);
      setOrganizationMemberships(orgResult.memberships);
      setStaffRole(derivedStaffRole);
    } finally {
      if (requestId === rolesRequestIdRef.current) {
        setRolesLoading(false);
      }
    }
  }, []);

  const refreshOrgMembership = async () => {
    if (!user) return;
    await refreshRolesForUser(user.id);
  };

  const switchOrganization = (organizationId: string) => {
    localStorage.setItem(SELECTED_ORG_STORAGE_KEY, organizationId);
    const selectedMembership = organizationMemberships.find((membership) => membership.organizationId === organizationId);
    if (selectedMembership) {
      setOrganizationMembership(selectedMembership);
      setIsOrgOwner(selectedMembership.role === 'owner');
      setStaffRole(isSuperAdmin ? 'super_admin' : selectedMembership.role === 'owner' ? 'owner' : 'cleaner');
    }
  };

  useEffect(() => {
    const initializedRef = { current: false };

    // 1) Get existing session first (prevents a transient null session from causing redirects)
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsUatReviewer(UAT_REVIEWER_EMAILS.has((session?.user.email ?? '').toLowerCase()));

      if (session?.user) {
        refreshRolesForUser(session.user.id);
      } else {
        setRolesLoading(false);
      }

      initializedRef.current = true;
      setLoading(false);
    });

    // 2) Subscribe to changes after we've attempted initial hydration
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      // Ignore early nulls before initial getSession() resolves
      if (!initializedRef.current && !nextSession) return;

      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setIsUatReviewer(UAT_REVIEWER_EMAILS.has((nextSession?.user.email ?? '').toLowerCase()));

      if (nextSession?.user) {
        refreshRolesForUser(nextSession.user.id);
      } else {
        // Reset roles state
        rolesRequestIdRef.current++;
        setIsSuperAdmin(false);
        setIsUatReviewer(false);
        setStaffRole(null);
        setIsOrgOwner(false);
        setOrganizationMembership(null);
        setOrganizationMemberships([]);
        setRolesLoading(false);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [refreshRolesForUser]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error };
  };

  const signOut = async () => {
    // Run registered pre-sign-out hooks (e.g. close the authoritative server-side
    // support view session) while the access token is still valid. Inspect their
    // errors so a failed cleanup is visible, but do not block sign-out on them.
    const hookErrors = await runPreSignoutHooks();
    if (hookErrors.length > 0) {
      console.error('Pre-sign-out hook(s) reported errors', hookErrors);
    }
    await supabase.auth.signOut();
    setIsSuperAdmin(false);
    setStaffRole(null);
    setIsOrgOwner(false);
    setOrganizationMembership(null);
    setOrganizationMemberships([]);
    localStorage.removeItem(SELECTED_ORG_STORAGE_KEY);
  };

  const resetPassword = async (email: string) => {
    // Send the recovery link through the Resend-backed Edge Function rather than
    // Supabase Auth's built-in mailer. The function generates the `recovery`
    // action link server-side and emails it; the link is never returned here.
    const { error } = await supabase.functions.invoke('send-password-reset', {
      body: { email: email.trim() },
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      isPlatformAdmin: isSuperAdmin,
      isUatReviewer,
      isSuperAdmin,
      staffRole,
      currentOrgRole: organizationMembership?.role ?? null,
      isOrgOwner,
      organizationMembership,
      organizationMemberships,
      loading,
      rolesLoading,
      signIn,
      signUp,
      signOut,
      resetPassword,
      updatePassword,
      refreshOrgMembership,
      switchOrganization
    }}>
      {children}
    </AuthContext.Provider>
  );
};
